import React from "react";
import { motion } from "framer-motion";

/**
 * TimelineSparkline - A premium development timeline visualization.
 */
const TimelineSparkline = ({ phases = [], total = 0 }) => {
    if (!phases || phases.length === 0) return null;

    const safeTotal = total || phases.reduce((s, p) => s + p.months, 0);

    return (
        <div className="space-y-6 py-2">
            {/* Total Duration Hero */}
            <div className="flex items-center justify-between">
                <div>
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">Estimated Duration</span>
                    <div className="flex items-baseline gap-1.5">
                        <span className="text-3xl font-black text-slate-800 tracking-tighter">{safeTotal}</span>
                        <span className="text-sm font-bold text-slate-400 uppercase tracking-wide">Months</span>
                    </div>
                </div>
            </div>

            {/* Sparkline Bar */}
            <div className="h-4 w-full bg-slate-100 rounded-full flex overflow-hidden border border-slate-200/50 shadow-inner">
                {phases.map((phase, i) => (
                    <motion.div
                        key={i}
                        initial={{ width: 0 }}
                        animate={{ width: `${(phase.months / safeTotal) * 100}%` }}
                        transition={{ duration: 1, delay: i * 0.1, ease: "easeOut" }}
                        className="h-full relative group"
                        style={{ backgroundColor: phase.color || "#6366f1" }}
                    >
                        {/* Tooltip on hover */}
                        <div className="absolute opacity-0 group-hover:opacity-100 transition-opacity bottom-full left-1/2 -translate-x-1/2 mb-2 bg-slate-900 text-white text-[10px] font-bold py-1 px-2 rounded whitespace-nowrap z-20 pointer-events-none">
                            {phase.name}: {phase.months} mo
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Legend / Breakdown */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {phases.map((phase, i) => (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 + i * 0.1 }}
                        className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50 border border-slate-100 hover:bg-white hover:shadow-sm transition-all group"
                    >
                        <div className="flex items-center gap-2">
                            <div 
                                className="w-2.5 h-2.5 rounded-full shadow-sm" 
                                style={{ backgroundColor: phase.color || "#6366f1" }}
                            />
                            <span className="text-[11px] font-bold text-slate-600 uppercase tracking-tight group-hover:text-slate-900 transition-colors">
                                {phase.name}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="text-xs font-black text-slate-800">{phase.months}</span>
                            <span className="text-[10px] font-bold text-slate-400">MO</span>
                        </div>
                    </motion.div>
                ))}
            </div>
        </div>
    );
};

export default TimelineSparkline;
